/*!
* Identifiers Plugin
* Manages stable identifiers related to the end-customer.
*
* Originally ported from: https://bitbucket.org/inindca/predictive-engagement-sdk/src/master/src/cookies.js
*/

// TODO: add unit tests

import {
  v1 as uuidv1,
  v4 as uuidv4,
} from 'uuid';
import uuidTime from 'uuid-time';
import { Promise } from 'es6-promise-polyfill'; // Each usage should import it locally. This is to avoid exposing it globally.
import {
  clone,
  isNil,
  isValidUuid,
  getStoragePlugin,
} from 'util';

const DEFAULT_EXPIRY_CUSTOMER_ID_SECS = 31536000; // 1 year
const DEFAULT_EXPIRY_SESSION_ID_SECS = 1800; // 30 minutes
const EXPIRY_SESSION_UUID_MSECS = 24 * 60 * 60 * 1000; // 1 day

const CUSTOMER_ID = 'CUSTOMER_ID';
const CUSTOMER_ID_TYPE = 'cookie';
const SESSION_ID = 'SESSION_ID';
const SESSION_ID_TYPE = 'web';
const SESSION_TIMESTAMPS = 'SESSION_TS';
const SESSION_TIMESTAMPS_REGEX = /^\d+\.\d+\.\d+$/;
const SESSION_COUNT = 'SESSION_COUNT';

let bProcessing = false;

const IDENTIFIERS = {
  [CUSTOMER_ID]: {
    name: 'actmu',
    description: 'Stable identifier of an anonymous end-customer.',
  },
  [SESSION_ID]: {
    name: 'actms',
    description: 'Identifier of the current web session.',
  },
  [SESSION_TIMESTAMPS]: {
    name: 'actts',
    description: 'First, previous and current session start timestamps. [first.previous.current]',
  },
  [SESSION_COUNT]: {
    name: 'actvc',
    description: 'The total number of sessions.',
  },
};
const CLEARED_REJECTION_MESSAGE = 'Messenger is cleared. Please reload the page to reinitialize.';
const NEW_SESSION_ID_EVENT = 'newSessionId';
const NEW_CUSTOMER_ID_EVENT = 'newCustomerId';
const IDENTIFIERS_PURGED_EVENT = 'identifiersPurged';
const IDENTIFIERS_CLEARED_EVENT = 'cleared';

Genesys('registerPlugin', 'Identifiers', (Identifiers) => {
  const $$ = Identifiers.data;
  const sStoragePlugin = getStoragePlugin(); // TODO: Temp to allow switching between localstorage and cookies. Hard code it to localstorage after the testing phase.
  let bIdentifiersCleared = false;

  /*
  * Configures all settings related to secured localstorage management
  */
  function setupIdentifiers(opts) {
    const options = opts || {};

    $$('customerIdExpiryTime', options.customerIdExpiryTime || DEFAULT_EXPIRY_CUSTOMER_ID_SECS); // time in seconds for the customer localstorage to expire
    $$('sessionIdExpiryTime', Math.min($$('customerIdExpiryTime'), options.sessionIdExpiryTime || DEFAULT_EXPIRY_SESSION_ID_SECS)); // time in seconds for the session localstorage to expire

    // ensure cookies on www.* are removed
    const cookieDomain = $$('Cookies.domain');
    const wwwDomain = `www.${cookieDomain}`;

    if (sStoragePlugin === 'Cookies') { // TODO: To be removed after migrating to localstorage only
      storageCommand('remove', CUSTOMER_ID, { domain: wwwDomain, recursive: false });
      storageCommand('remove', SESSION_ID, { domain: wwwDomain, recursive: false });
    }
  }

  function storageCommand(cmd, key, args = {}) {
    args.name = IDENTIFIERS[key].name;

    return Identifiers.command(`${sStoragePlugin}.${cmd}`, args);
  }

  function isValidIdentifier(id) {
    return !isNil(id) && isValidUuid(id);
  }

  function isUuidOlderThan24Hours(uuid) {
    try {
      return Date.now() - EXPIRY_SESSION_UUID_MSECS > uuidTime.v1(uuid);
    } catch (err) {
      // Handles the case where uuid passed is not valid v1 uuid
      return false;
    }
  }

  /*
  * Retrieves or automatically generates the customer and session cookies.
  * Handles refreshing the expiry time of cookies.
  */
  function getOrCreateIdentifiers(opts) {
    const options = opts || {};
    const {
      forceNewCustomer = false,
      forceNewSession = false,
      touchDisabled = false,
      createDisabled = false,
    } = options;

    let customerId;
    let sessionId;
    let isNewSession = false;
    let isNewCustomer = false;
    const domain = $$('Cookies.domain');

    if (!bProcessing) {
      bProcessing = true;

      return storageCommand('get', CUSTOMER_ID)
        .then((cid) => {
          if (createDisabled) {
            customerId = isValidIdentifier(cid) ? cid : null;
          } else {
            isNewCustomer = forceNewCustomer || !isValidIdentifier(cid);
            customerId = isNewCustomer ? uuidv4() : cid;
          }

          if (touchDisabled && !isNewCustomer || !isValidIdentifier(customerId)) {
            return Promise.resolve();
          }

          return storageCommand('set', CUSTOMER_ID, { value: customerId, domain, expireInSecs: $$('customerIdExpiryTime') });
        })
        .then(() => storageCommand('get', SESSION_ID))
        .then((sid) => {
          if (!isValidIdentifier(customerId)) {
            sessionId = null;

            return storageCommand('remove', SESSION_ID);
          }

          if (createDisabled) {
            sessionId = isValidIdentifier(sid) && !isUuidOlderThan24Hours(sid) ? sid : null;
          } else {
            isNewSession = forceNewSession || isNewCustomer || !isValidIdentifier(sid) || isUuidOlderThan24Hours(sid);
            sessionId = isNewSession ? uuidv1() : sid;
          }

          if (touchDisabled && !isNewSession || !isValidIdentifier(sessionId)) {
            return Promise.resolve();
          }

          return storageCommand('set', SESSION_ID, { value: sessionId, domain, expireInSecs: $$('sessionIdExpiryTime') });
        })
        .then(() => {
          if (!isNewSession) {
            return Promise.resolve();
          }

          return Promise.all([
            storageCommand('get', SESSION_TIMESTAMPS),
            storageCommand('get', SESSION_COUNT),
          ]).then(([ts, sc]) => {
            const nowInSeconds = Math.ceil(Date.now() / 1000);
            let first;
            let previous;
            let current;
            let sessionCount = parseInt(sc, 10) || 0;

            if (isNewCustomer) {
              first = nowInSeconds;
              previous = nowInSeconds;
              current = nowInSeconds;
              sessionCount = 1;
            } else {
              // when updating stored timestamps when new session begins, the first session timestamp is not changing,
              // previous session timestamp gets discarded, current session timestamp becomes new previous timestamp
              // and finally new current session timestamp gets calculated
              ({ first, previous, current } = tsStringToTimestamps(ts));
              previous = current;
              current = nowInSeconds;
              sessionCount++;
            }

            return Promise.all([
              storageCommand('set', SESSION_TIMESTAMPS, {
                value: `${first}.${previous}.${current}`,
                domain: $$('Cookies.domain'),
                expireInSecs: $$('customerIdExpiryTime'),
              }),
              storageCommand('set', SESSION_COUNT, {
                value: sessionCount,
                domain: $$('Cookies.domain'),
                expireInSecs: $$('customerIdExpiryTime'),
              }),
            ]);
          });
        }).then(() => {
          const ids = {
            customerId,
            sessionId,
          };

          if (isNewSession) {
            Identifiers.publish(NEW_SESSION_ID_EVENT, clone(ids));
          }

          if (isNewCustomer) {
            Identifiers.publish(NEW_CUSTOMER_ID_EVENT, clone(ids));
          }

          bProcessing = false; // reset

          return ids;
        });
    }
  }

  function purgeAll() {
    return Promise.all([
      storageCommand('remove', CUSTOMER_ID),
      storageCommand('remove', SESSION_ID),
      storageCommand('remove', SESSION_TIMESTAMPS),
      storageCommand('remove', SESSION_COUNT),
    ]);
  }

  function getSessionCount() {
    return storageCommand('get', SESSION_COUNT);
  }

  function getSessionTimestamps() {
    return storageCommand('get', SESSION_TIMESTAMPS).then((ts) => tsStringToTimestamps(ts));
  }

  function tsStringToTimestamps(tsString) {
    const nowInSeconds = Math.ceil(Date.now() / 1000);
    let [first, previous, current] = [nowInSeconds, nowInSeconds, nowInSeconds];

    if (tsString && SESSION_TIMESTAMPS_REGEX.test(tsString)) {
      [first, previous, current] = tsString.split('.').map((v) => parseInt(v, 10) || 0);
    }

    return { first, previous, current };
  }

  /***************************************************************************/
  /******************** Functions that support commands **********************/
  /***************************************************************************/

  /**
   * Internal function that supports getJourneyContext command
   * @param {*} cmd
   * @returns promise
   */
  function getJourneyContext(cmd) {
    if (!bIdentifiersCleared) {
      return getOrCreateIdentifiers(cmd.data)
        .then(({ customerId, sessionId }) => {
          return cmd.resolve({
            customer: {
              id: customerId,
              idType: CUSTOMER_ID_TYPE,
            },
            customerSession: {
              id: sessionId,
              type: SESSION_ID_TYPE,
            },
          });
        })
        .catch(cmd.reject);
    } else cmd.reject(CLEARED_REJECTION_MESSAGE);
  }

  /**
   * Internal function that supports getSessionId command
   * @param {*} cmd
   * @returns promise
   */
  function getSessionId(cmd) {
    if (!bIdentifiersCleared) {
      return getOrCreateIdentifiers()
        .then(({ sessionId }) => {
          return cmd.resolve(sessionId);
        })
        .catch(cmd.reject);
    } else cmd.reject(CLEARED_REJECTION_MESSAGE);
  }

  /**
   * Internal function that supports getCustomerId command
   * @param {*} cmd
   * @returns promise
   */
  function getCustomerId(cmd) {
    if (!bIdentifiersCleared) {
      return getOrCreateIdentifiers()
        .then(({ customerId }) => {
          return cmd.resolve(customerId);
        })
        .catch(cmd.reject);
    } else cmd.reject(CLEARED_REJECTION_MESSAGE);
  }

  /**
   * Internal function that supports getAll command
   * @param {*} cmd
   * @returns promise
   */
  function getAll(cmd) {
    if (!bIdentifiersCleared) {
      return getOrCreateIdentifiers(cmd.data)
        .then(cmd.resolve)
        .catch(cmd.reject);
    } else cmd.reject(CLEARED_REJECTION_MESSAGE);
  }


  /***************************************************************************/
  /*************************** Register commands *****************************/
  /***************************************************************************/

  /*
  * Command: getSessionId
  * @description Provides an identifier for the current web session
  */

  Identifiers.registerCommand('getSessionId', (cmd) => {
    if (!bProcessing) { // WEBMESS-1713: if any previous request is processing, wait for it to complete and resolve later below.
      getSessionId(cmd);
    } else {
      const iInterval = setInterval(() => {
        if (!bProcessing) {
          getSessionId(cmd);
          clearInterval(iInterval);
        }
      }, 100);
    }
  });

  /*
  * Command: getCustomerId
  * @description Provides an identifier for the end-customer
  */

  Identifiers.registerCommand('getCustomerId', (cmd) => {
    if (!bProcessing) { // WEBMESS-1713: if any previous request is processing, wait for it to complete and resolve later below.
      getCustomerId(cmd);
    } else {
      const iInterval = setInterval(() => {
        if (!bProcessing) {
          getCustomerId(cmd);
          clearInterval(iInterval);
        }
      }, 100);
    }
  });

  /*
  * Command: getAll
  * @description Provides all supported identifiers
  */

  Identifiers.registerCommand('getAll', (cmd) => {
    if (!bProcessing) { // WEBMESS-1713: if any previous request is processing, wait for it to complete and resolve later below.
      getAll(cmd);
    } else {
      const iInterval = setInterval(() => {
        if (!bProcessing) {
          getAll(cmd);
          clearInterval(iInterval);
        }
      }, 100);
    }
  });

  /*
  * Command: getSessionTimestamps
  * @description Provides session start times as number of seconds since unix epoch (1970/01/01)
  */
  Identifiers.registerCommand('getSessionTimestamps', (cmd) => {
    if (!bIdentifiersCleared) {
      return getSessionTimestamps()
        .then(cmd.resolve)
        .catch(cmd.reject);
    } else cmd.reject(CLEARED_REJECTION_MESSAGE);
  });

  /*
  * Command: getSessionCount
  * @description Provides total session count for the current end-customer identifier
  */
  Identifiers.registerCommand('getSessionCount', (cmd) => {
    if (!bIdentifiersCleared) {
      return getSessionCount()
        .then(cmd.resolve)
        .catch(cmd.reject);
    } else cmd.reject(CLEARED_REJECTION_MESSAGE);
  });

  /*
  * Command: getJourneyContext
  * @description Provides identifiers in the JourneyContext format
  */

  Identifiers.registerCommand('getJourneyContext', (cmd) => {
    if (!bProcessing) {
      getJourneyContext(cmd);
    } else {
      const iInterval = setInterval(() => {
        if (!bProcessing) {
          getJourneyContext(cmd);
          clearInterval(iInterval);
        }
      }, 100);
    }
  });

  /*
  * Command: purgeAll
  * @description Removes all supported identifiers. Use if tracking consent has been revoked.
  */
  Identifiers.registerCommand('purgeAll', (cmd) => {
    if (!bIdentifiersCleared) {
      return purgeAll()
        .then(() => {
          Identifiers.publish(IDENTIFIERS_PURGED_EVENT);

          return cmd.resolve();
        })
        .catch(cmd.reject);
    } else cmd.reject(CLEARED_REJECTION_MESSAGE);
  });

  Identifiers.registerCommand('clear', (cmd) => {
    if (cmd && cmd.commander && cmd.commander !== 'Messenger') {
      cmd.reject(`${cmd.commander} plugin does not have permission to access clear identifiers plugin command`);
    }

    bIdentifiersCleared = true; //set to true to reject other commands

    return purgeAll()
      .then(() => {
        Identifiers.publish(IDENTIFIERS_CLEARED_EVENT);

        return cmd.resolve();
      })
      .catch(cmd.reject);
  });

  Identifiers.registerEvents([
    NEW_SESSION_ID_EVENT,
    NEW_CUSTOMER_ID_EVENT,
    IDENTIFIERS_PURGED_EVENT,
    IDENTIFIERS_CLEARED_EVENT,
  ]);

  Identifiers.subscribe(`${sStoragePlugin}.ready`, () => {
    setupIdentifiers();

    Identifiers.ready();
  });

  Identifiers.subscribe('Auth.authenticated', () => {
    getOrCreateIdentifiers();
  });
});
